import { apiClient } from '@/utils/api-client';

export interface ReportFilters {
  email_domain?: string;
  from_date?: string;
  to_date?: string;
  affiliate_id?: string;
  license_admin_id?: string;
  product_id?: string;
  activity_type?: string;
  org_type?: 'licensed' | 'unlicensed';
  include_dates?: boolean;
  result?: 'pass' | 'fail' | 'expired';
  search?: string;
  download?: boolean;
  limit?: number;
  export_format?: 'csv' | 'xlsx';
}

export interface PortalTotals {
  direct_users: number;
  license_admins: number;
  license_users: number;
  affiliate_admins: number;
  affiliate_users: number;
  prepopulated_users: number;
  self_added_users: number;
  active_last_30_days: number;
  total_users: number;
  generated_at: string;
}

/**
 * Generic download helper to eliminate duplication
 */
function formatDatetime(): string {
  const now = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return (
    `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}` +
    `_${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`
  );
}

async function downloadReport(endpoint: string, filters: ReportFilters = {}, filename?: string): Promise<void> {
  const cleanFilters = Object.fromEntries(
    Object.entries({ ...filters, download: 'true' }).filter(
      ([, value]) => value !== undefined && value !== ''
    )
  );
  const params = new URLSearchParams(cleanFilters as Record<string, string>).toString();

  const response = await fetch(
    `${import.meta.env.VITE_API_BASE_URL}${endpoint}?${params}`,
    { credentials: 'include' }
  );

  if (!response.ok) {
    const body = await response.json().catch(() => null);
    throw new Error(body?.errors?.[0]?.title ?? 'Failed to download report. Please try again.');
  }

  const blob = await response.blob();
  const resolvedFilename = filename ?? `report_${formatDatetime()}.${filters.export_format ?? 'csv'}`;

  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = resolvedFilename;
  document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(url);
  document.body.removeChild(a);
}

export const reportsService = {
  // User Reports
  async getPortalTotals(reportDate?: string) {
    const params = reportDate ? { report_date: reportDate } : {};
    return apiClient.get('/api/admin/reports/users/portal_totals', params);
  },

  async downloadPortalTotals() {
    return downloadReport('/api/admin/reports/users/portal_totals');
  },

  async getUserListByTool(filters: ReportFilters) {
    return apiClient.get('/api/admin/reports/users/user_list_by_tool', filters);
  },

  async downloadUserListByTool(filters: ReportFilters) {
    return downloadReport('/api/admin/reports/users/user_list_by_tool', filters);
  },

  async getPortalActivity(filters: ReportFilters) {
    return apiClient.get('/api/admin/reports/users/portal_activity', filters);
  },

  async downloadPortalActivity(filters: ReportFilters) {
    return downloadReport('/api/admin/reports/users/portal_activity', filters);
  },

  async getLoginCount(filters: ReportFilters) {
    return apiClient.get('/api/admin/reports/users/login_count', filters);
  },

  async downloadLoginCount(filters: ReportFilters) {
    return downloadReport('/api/admin/reports/users/login_count', filters);
  },

  async getElearningTracking(filters: ReportFilters) {
    return apiClient.get('/api/admin/reports/users/elearning_tracking', filters);
  },

  async downloadElearningTracking(filters: ReportFilters) {
    return downloadReport('/api/admin/reports/users/elearning_tracking', filters);
  },

  async getUserList(filters: ReportFilters) {
    return apiClient.get('/api/admin/reports/users/user_list', filters);
  },

  async downloadUserList(filters: ReportFilters) {
    return downloadReport('/api/admin/reports/users/user_list', filters);
  },

  async getDeletedUsers(filters: ReportFilters) {
    return apiClient.get('/api/admin/reports/users/deleted_users', filters);
  },

  async downloadDeletedUsers(filters: ReportFilters) {
    return downloadReport('/api/admin/reports/users/deleted_users', filters);
  },

  async getWorkspaceEntitlements(filters: ReportFilters) {
    return apiClient.get('/api/admin/reports/users/workspace_entitlements', filters);
  },

  async downloadWorkspaceEntitlements(filters: ReportFilters) {
    return downloadReport('/api/admin/reports/users/workspace_entitlements', filters);
  },

  async downloadUserActivity(filters: ReportFilters) {
    return downloadReport('/api/admin/reports/users/user_activity', filters);
  },

  // Evaluations Reports (Prosci Admin only)
  async getPcacpExamResults(filters: ReportFilters) {
    return apiClient.get('/api/admin/reports/evaluations/pcacp_exam_results', filters);
  },

  async downloadPcacpExamResults(filters: ReportFilters) {
    return downloadReport('/api/admin/reports/evaluations/pcacp_exam_results', filters, `pcacp_report_${formatDatetime()}.csv`);
  },

  async getPcacpExamSession(sessionId: string | number) {
    return apiClient.get(`/api/admin/reports/evaluations/pcacp_exam_session/${sessionId}`);
  },

  async downloadPcacpExamSession(sessionId: string | number) {
    return downloadReport(
      `/api/admin/reports/evaluations/pcacp_exam_session/${sessionId}`,
      {},
      `pcacp_exam_session_${sessionId}_${formatDatetime()}.csv`
    );
  },

  // Organization Reports
  async getOrganizationList(filters: ReportFilters) {
    return apiClient.get('/api/admin/reports/organizations/organization_list', filters);
  },

  async downloadOrganizationList(filters: ReportFilters) {
    return downloadReport('/api/admin/reports/organizations/organization_list', filters);
  },
};
